/**
 * Application shell: session gate, theme toggle, top navigation and routing.
 *
 * Authentication is the HttpOnly session cookie, so the shell probes
 * `GET /api/v1/auth/me` on mount; a 401 renders the login page instead of the
 * app. The City, Asset and dependency-Graph surfaces (spec §5.4-5.6) are built as
 * self-contained modules and routed here per `web/INTEGRATION-NOTES.md`.
 */

import { useCallback, useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ApiError, api } from './api.js'
import { setTransitions } from './lib/filters.js'
import { TaxonomyProvider } from './lib/taxonomy.jsx'
import AssetsPage from './views/assets/AssetsPage.jsx'
import Board from './views/Board.jsx'
import CityPage, { CityIndex } from './views/city/CityPage.jsx'
import GraphPage from './views/graph/GraphPage.jsx'
import List from './views/List.jsx'
import Login from './views/Login.jsx'
import Ticket from './views/Ticket.jsx'

const THEME_KEY = 'todo.theme'

/** Apply and persist the colour theme. */
function useTheme() {
  const [theme, setTheme] = useState(
    () =>
      localStorage.getItem(THEME_KEY) ||
      (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  )
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(THEME_KEY, theme)
  }, [theme])
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))]
}

export default function App() {
  const [session, setSession] = useState({ state: 'loading', user: null })
  const [metaError, setMetaError] = useState(null)
  const [theme, toggleTheme] = useTheme()
  const location = useLocation()

  useEffect(() => {
    api
      .me()
      .then((user) => setSession({ state: 'in', user }))
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) {
          setSession({ state: 'out', user: null })
        } else {
          setSession({ state: 'error', user: null, error })
        }
      })
  }, [])

  // Status-transition policy belongs to the server. Fetch it once and install
  // it, so the board and the ticket page can never dim a legal move (or offer
  // an illegal one) because a client-side copy went stale.
  useEffect(() => {
    api
      .meta()
      .then((meta) => {
        setTransitions(meta.transitions)
        setMetaError(null)
      })
      .catch((error) => setMetaError(error))
  }, [session.state])

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {})
    setSession({ state: 'out', user: null })
  }, [])

  if (session.state === 'loading') return <div className="notice">Loading…</div>
  if (session.state === 'error') {
    return <div className="notice error">Cannot reach the API: {session.error.message}</div>
  }
  if (session.state === 'out') {
    return (
      <div className="app">
        <Login onSignedIn={(user) => setSession({ state: 'in', user })} />
      </div>
    )
  }

  const search = location.search

  return (
    <TaxonomyProvider>
      <div className="app">
        <header className="topbar">
          <div className="brand">
            TODO <span>· placemetry</span>
          </div>
          <nav className="nav">
            <NavLink to={{ pathname: '/', search }} end>
              Board
            </NavLink>
            <NavLink to={{ pathname: '/list', search }}>List</NavLink>
            <NavLink to="/cities">Cities</NavLink>
            <NavLink to="/assets">Assets</NavLink>
            <NavLink to="/graph">Graph</NavLink>
          </nav>
          <span className="spacer" />
          <span className="who">{session.user.name || session.user.email}</span>
          <button type="button" className="btn sm ghost" onClick={toggleTheme}>
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          <button type="button" className="btn sm ghost" onClick={signOut}>
            Sign out
          </button>
        </header>
        <main className="main">
          {metaError ? (
            <div className="notice error">
              Could not load the status-transition table from the server
              ({metaError.message}). Status changes stay disabled until it loads —
              the client keeps no copy of its own.
            </div>
          ) : null}
          <Routes>
            <Route path="/" element={<Board />} />
            <Route path="/list" element={<List />} />
            <Route path="/t/:key" element={<Ticket />} />
            <Route path="/cities" element={<CityIndex />} />
            <Route path="/city/:slug" element={<CityPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/graph" element={<GraphPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </TaxonomyProvider>
  )
}
